import React, { useState, useEffect } from 'react';
import { X, Download, FileText, Users, CheckCircle } from 'lucide-react';
import './ExportModal.css';

interface ExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (format: 'csv' | 'vcard') => void;
  totalContacts: number;
}

const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onClose,
  onExport,
  totalContacts
}) => {
  const [format, setFormat] = useState<'csv' | 'vcard'>('csv');
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormat('csv');
      setIsExporting(false);
      setExportSuccess(false);
    }
  }, [isOpen]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await onExport(format);
      setExportSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Export failed:', error);
      setIsExporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="export-modal-overlay" onClick={onClose}>
      <div className="export-modal" onClick={(e) => e.stopPropagation()}>
        <div className="export-modal-header">
          <div className="export-modal-header-content">
            <Download className="export-modal-icon" />
            <h2>Export Contacts</h2>
          </div>
          <button className="export-modal-close" onClick={onClose} disabled={isExporting}>
            <X size={24} />
          </button>
        </div>

        {exportSuccess ? (
          <div className="export-success">
            <CheckCircle size={48} className="export-success-icon" />
            <h3>Export Successful!</h3>
            <p>Your contacts have been downloaded.</p>
          </div>
        ) : (
          <>
            <div className="export-modal-body">
              {/* Format Selection */}
              <div className="export-section">
                <h3 className="export-section-title">Export Format</h3>
                <div className="export-options">
                  <label className={`export-option ${format === 'csv' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="format"
                      value="csv"
                      checked={format === 'csv'}
                      onChange={() => setFormat('csv')}
                      disabled={isExporting}
                    />
                    <div className="export-option-content">
                      <FileText size={24} />
                      <div className="export-option-text">
                        <strong>CSV</strong>
                        <span>Spreadsheet format (Excel, Google Sheets)</span>
                      </div>
                    </div>
                  </label>

                  <label className={`export-option ${format === 'vcard' ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="format"
                      value="vcard"
                      checked={format === 'vcard'}
                      onChange={() => setFormat('vcard')}
                      disabled={isExporting}
                    />
                    <div className="export-option-content">
                      <Users size={24} />
                      <div className="export-option-text">
                        <strong>vCard</strong>
                        <span>Individual .vcf files downloaded separately</span>
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Export Summary */}
              <div className="export-summary">
                <p>
                  Exporting <strong>{totalContacts}</strong> contact{totalContacts !== 1 ? 's' : ''} as <strong>{format.toUpperCase()}</strong>
                  {format === 'vcard' && totalContacts > 1 && (
                    <span className="export-note"> (each as separate file)</span>
                  )}
                </p>
              </div>
            </div>

            <div className="export-modal-footer">
              <button 
                className="export-modal-button secondary" 
                onClick={onClose}
                disabled={isExporting}
              >
                Cancel
              </button>
              <button 
                className="export-modal-button primary"
                onClick={handleExport}
                disabled={isExporting}
              >
                {isExporting ? (
                  <>
                    <span className="spinner-small"></span>
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download size={20} />
                    Export
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ExportModal;
